'use client';

import { getProblemTypeTitle } from '@/constants/problem-type-titles';
import { ReviewAiApiProblemEntity, ReviewAiApiResponseEntity } from '@/entities/review.entity';
import Modal from '@/ui/Modal';
import clsx from 'clsx';
import { FC } from 'react';

type ModalAiResponseDataProps = {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
    response: ReviewAiApiResponseEntity;
};

const sentimentLabelMap: Record<string, string> = {
    positive: 'Позитив',
    negative: 'Негатив',
    neutral: 'Нейтрально',
};

const sentimentClassMap: Record<string, string> = {
    positive: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    negative: 'border-rose-200 bg-rose-50 text-rose-700',
    neutral: 'border-slate-200 bg-slate-100 text-slate-700',
};

const normalizeProblems = (value: unknown): ReviewAiApiProblemEntity[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is ReviewAiApiProblemEntity => {
        return typeof item === 'object' && item !== null;
    });
};

const renderValue = (value: string | number | undefined | null) => {
    if (value === undefined || value === null || value === '') {
        return '—';
    }

    return value;
};

const ModalAiResponseData: FC<ModalAiResponseDataProps> = ({ isShown, setIsShown, response }) => {
    const reviewData = response.review;
    const usageData = response.usage;
    const problems = normalizeProblems(reviewData?.problems);

    return (
        <Modal
            isShown={isShown}
            setIsShown={setIsShown}
            title="AI анализ отзыва"
            closable
            className="h-[88vh] w-[92vw] max-w-[1000px]"
        >
            <div className="h-full w-full min-h-0 overflow-auto p-4">
                <div className="flex flex-col gap-4 text-sm leading-5 text-slate-800">
                    <section className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="grid gap-2 md:grid-cols-3">
                            <div>
                                <p className="text-xs text-slate-500">Тип</p>
                                <p className="font-medium">
                                    {renderValue(getProblemTypeTitle(reviewData?.type_primary))}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Главная проблема</p>
                                <p className="font-medium">
                                    {renderValue(getProblemTypeTitle(reviewData?.problem_primary))}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Вторичная проблема</p>
                                <p className="font-medium">
                                    {renderValue(getProblemTypeTitle(reviewData?.problem_secondary))}
                                </p>
                            </div>
                        </div>
                        <div className="mt-3">
                            <p className="text-xs text-slate-500">Черновик ответа из аналитики</p>
                            <p className="whitespace-pre-line break-words">{renderValue(reviewData?.answer)}</p>
                        </div>
                    </section>

                    <section className="flex flex-col gap-3">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Проблемы</h3>
                        {problems.length ? (
                            <div className="flex flex-col gap-2">
                                {problems.map((problem, index) => {
                                    const sentiment = problem.sentiment || '';
                                    const sentimentLabel = sentimentLabelMap[sentiment] || sentiment || '—';

                                    return (
                                        <article
                                            key={`${problem.problem || 'problem'}-${index}`}
                                            className="rounded-lg border border-slate-200 p-3"
                                        >
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <p className="font-semibold">
                                                    {renderValue(getProblemTypeTitle(problem.problem))}
                                                </p>
                                                <span
                                                    className={clsx(
                                                        'rounded-full border px-2 py-0.5 text-xs',
                                                        sentimentClassMap[sentiment] || sentimentClassMap.neutral,
                                                    )}
                                                >
                                                    {sentimentLabel}
                                                </span>
                                            </div>
                                            <div className="mt-2 grid gap-2 text-xs text-slate-600 md:grid-cols-3">
                                                <p>Intensity: {renderValue(problem.intensity)}</p>
                                                <p>Actionability: {renderValue(problem.actionability)}</p>
                                                <p>Confidence: {renderValue(problem.confidence)}</p>
                                            </div>
                                            <div className="mt-2 space-y-1 text-xs text-slate-700">
                                                <p>
                                                    <span className="text-slate-500">Evidence: </span>
                                                    {renderValue(problem.evidence)}
                                                </p>
                                                <p>
                                                    <span className="text-slate-500">Model: </span>
                                                    {renderValue(problem.market_model)}
                                                </p>
                                                <p>
                                                    <span className="text-slate-500">Model desc: </span>
                                                    {renderValue(problem.model_desc)}
                                                </p>
                                                <p>
                                                    <span className="text-slate-500">Action: </span>
                                                    {renderValue(problem.proposed_action)}
                                                </p>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500">
                                Нет данных по проблемам
                            </p>
                        )}
                    </section>

                    <section className="rounded-lg border border-slate-200 bg-white p-3">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Usage</h3>
                        <div className="mt-2 grid gap-2 text-xs text-slate-700 md:grid-cols-3">
                            <p>Prompt: {renderValue(usageData?.prompt_tokens)}</p>
                            <p>Completion: {renderValue(usageData?.completion_tokens)}</p>
                            <p>Total: {renderValue(usageData?.total_tokens)}</p>
                        </div>
                        <p className="mt-2 text-xs text-slate-600">
                            Reasoning: {renderValue(usageData?.reasoning_tokens)}
                        </p>
                    </section>

                    <details className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <summary className="cursor-pointer text-xs font-medium text-slate-700">Raw JSON</summary>
                        <pre className="mt-2 overflow-auto rounded bg-white p-2 text-[11px] leading-4 text-slate-800">
                            {JSON.stringify(response, null, 2)}
                        </pre>
                    </details>
                </div>
            </div>
        </Modal>
    );
};

export default ModalAiResponseData;
